import { useEffect, useRef } from 'react';
import type { Product } from '../api';
import type { BreadcrumbData } from '@trg/api';

export type BuriedPointData = Array<Record<Catalog, BuriedPoint>>;

export interface BuriedPoint {
  // 商品目錄
  catalog_item_list_id: string;
  catalog_item_list_name: string;
  // 廣告展示 如果是促銷廣告 ad_開頭4個欄位為必填
  ad_creative_slot: string;
  ad_creative_name: string;
  ad_promotion_id: string;
  ad_promotion_name: string;
  // 選擇折價卷
  select_coupon: string;
  // 選擇付款方式
  select_payment_type: string;
  // 完成購賣
  purchase_transaction_id: string;
  purchase_shipping: string;
  purchase_tax: string;
  // 如果有商品則帶入商品資料, 無商品不用填
  items: BuriedPointProduct[];
}

export interface BuriedPointProduct {
  item_id: string;
  item_name: string;
  item_brand: '特力屋';
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  price: number;
  quantity: number;
}

export type BuriedPointType = 'ad' | 'prodlist';

export type Catalog = `${BuriedPointType}${number}`;

export type TagAttributesGenerator = (
  item?: string
) => Partial<TagAttributes> | null;

export type TagAttributes = Pick<HTMLElement, 'id'> & {
  'data-id-catalog': Catalog;
  'data-id-id': DatasetIdEnum | DatasetIdEnumFroChild | BuriedPointBehavior;
  'data-id-item': Product['sku'];
};

export enum DatasetIdEnum {
  'ad' = 'promotion',
  'prodlist' = 'listview',
}

export enum DatasetIdEnumFroChild {
  'ad' = 'promotion',
  'prodlist' = 'prodlist',
}

export type BuriedPointBehavior = 'addToWishlist' | 'addToCart';

const globalWindow = window as typeof window & { _dtpd?: BuriedPointData };

function createBuriedPointTemplate(): BuriedPoint {
  return {
    catalog_item_list_id: '',
    catalog_item_list_name: '',
    ad_creative_slot: '',
    ad_creative_name: '',
    ad_promotion_id: '',
    ad_promotion_name: '',
    select_coupon: '',
    select_payment_type: '',
    purchase_transaction_id: '',
    purchase_shipping: '',
    purchase_tax: '',
    items: [],
  };
}

function getBuriedPointTypeCount(type: BuriedPointType) {
  const buriedPointData = globalWindow._dtpd ?? [];
  const { length } = buriedPointData.filter((item) =>
    Object.keys(item).some((i) => i.startsWith(type))
  );
  return length + 1;
}

function checkingCatalogIsExist(key: Catalog) {
  const buriedPointData = globalWindow._dtpd ?? [];
  return buriedPointData.some((item) => key in item);
}

function getPrice<T extends Pick<Product, 'listPrice' | 'salePrice'>>({
  listPrice,
  salePrice,
}: T) {
  const price = salePrice ?? listPrice;
  return Number(price.split('-')?.at(0)?.replace(/[$,]/g, ''));
}

function getCategores<T extends BreadcrumbData>(data: T[] = []) {
  return data.map(({ link }) => link?.split('/').at(-1) ?? '');
}

export function generateBuriedPointProducts(
  products: Pick<Product, 'sku' | 'name' | 'listPrice' | 'salePrice'>[] = [],
  breadcrumbs: BreadcrumbData[] = []
): BuriedPointProduct[] {
  try {
    const categores = getCategores(breadcrumbs);
    return products.map((item) => ({
      item_id: item.sku,
      item_name: item.name,
      item_brand: '特力屋',
      item_category: categores.at(0) ?? '',
      item_category2: categores.at(1) ?? '',
      item_category3: categores.at(2) ?? '',
      item_category4: categores.at(3) ?? '',
      item_category5: categores.at(4) ?? '',
      price: getPrice(item),
      quantity: 1,
    }));
  } catch (error) {
    console.warn('generate burned point error:', error);
    return [];
  }
}

export interface UseBuriedPointOptions {
  data?: Partial<BuriedPoint>;
  enabled?: boolean;
}

export function useBuriedPoint(
  type: BuriedPointType,
  options: UseBuriedPointOptions
) {
  const { enabled = true, data } = options;

  const catalogRef = useRef<Catalog>();

  const append = () => {
    try {
      if (catalogRef.current && checkingCatalogIsExist(catalogRef.current)) {
        throw new Error(`Buried point already initialized`);
      }
      globalWindow._dtpd = globalWindow._dtpd ?? [];
      const count = getBuriedPointTypeCount(type);
      const catalog: Catalog = `${type}${count}`;
      const template = createBuriedPointTemplate();
      globalWindow._dtpd.push({ [catalog]: Object.assign(template, data) });
      catalogRef.current = catalog;
    } catch (error) {
      console.warn(`append burned point error:`, error);
    }
  };

  const update = () => {
    try {
      if (!catalogRef.current || !checkingCatalogIsExist(catalogRef.current)) {
        throw new Error(`Buried point not initialized`);
      }
      globalWindow._dtpd = globalWindow._dtpd ?? [];
      const catalog = catalogRef.current;
      const index = globalWindow._dtpd.findIndex((item) => catalog in item);
      if (index === -1) {
        throw new Error(`Buried point not found`);
      }
      const item = globalWindow._dtpd[index];
      const prev = item[catalogRef.current as Catalog];
      Object.assign(prev, data);
    } catch (error) {
      console.warn(`update burned point error:`, error);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    catalogRef.current ? update() : append();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabled]);

  const tagAttributes: Partial<TagAttributes> = enabled
    ? {
        id: catalogRef.current,
        'data-id-id': DatasetIdEnum[type],
        'data-id-catalog': catalogRef.current,
      }
    : {};

  return {
    data: globalWindow._dtpd,
    catalog: catalogRef.current,
    tagAttributes,
    append,
    update,
  } as const;
}
